import { useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTender } from "../contexts/TenderContext";
import { useGlobalSettings } from "./useGlobalSettings";
import { useItemLibrary } from "./useItemLibrary";
import { useGetRetainingWallsBuildUp } from "./useRetainingWallsBuildUp";
import { useGetGeoToberWallBuildUp } from "./useWallSectionBuildUp";
import { RateMappingJSON } from "../types/rateMappingTypes";
import { calculateSumAnalysisGrandTotal } from "../utils/sumAnalysisGrandTotalCalculator";
import { getNumericBoqVersion } from "../utils/boqVersionPrefix";

export interface SectionTotal {
  name: string;
  stepName: string;
  baseAmount: number;
  preliminariesAmount: number;
  ohpAmount: number;
  totalAmount: number;
}

export interface GroundworksTotals {
  /** Individual section totals with breakdown */
  sections: SectionTotal[];
  /** Sum of all section base amounts (before adjustments) */
  grandTotal: number;
  /** Sum of all section totals (including preliminaries and OHP per section) */
  grandTotalWithAdjustments: number;
  /** Discount amount applied to grand total */
  discountAmount: number;
  /** Final tender total (grand total with adjustments minus discount) */
  tenderTotal: number;
  /** Preliminaries percentage from settings */
  preliminariesPercent: number;
  /** OH&P percentage from settings */
  ohpPercent: number;
  /** Discount percentage from settings */
  discountPercent: number;
}

interface UseGroundworksTotalCalculationsOptions {
  /** Whether to include preliminaries in calculations */
  includePreliminaries?: boolean;
  /** Whether to include OH&P in calculations */
  includeOhp?: boolean;
  /** Whether to include discount in calculations */
  includeDiscount?: boolean;
  /** Optional external recalculation trigger */
  recalculateTrigger?: number;
}

/**
 * Hook that calculates all groundworks totals following the same logic as SumAnalysis Bill Summary.
 * This provides a single source of truth for groundworks total calculations.
 *
 * @param options - Configuration options for which adjustments to include
 * @returns GroundworksTotals object with all calculated values
 */
export const useGroundworksTotalCalculations = (
  options: UseGroundworksTotalCalculationsOptions = {}
): GroundworksTotals => {
  const {
    includePreliminaries = false,
    includeOhp = false,
    includeDiscount = false,
    recalculateTrigger = 0,
  } = options;

  const { state } = useTender();
  const { settings: globalSettings } = useGlobalSettings();
  const { data: itemLibraryData } = useItemLibrary(state.region || "");
  const { pathname } = useLocation();

  // Mirror SumAnalysis: fetch wall build-up specs from the API so wall section
  // totals match what Sum Analysis displays. Only fires when a project is loaded.
  const tenderName = state.projectName || "";
  const boqVersionNumber = useRef<number>((() => {
    const raw = localStorage.getItem("currentBoqVersion");
    // version may be stored as "GWKS-3"/"RS-3" (set by /version-history) — must
    // extract the plain numeric part first, otherwise wall build-up specs are
    // fetched for the wrong version (or not at all), diverging from Sum Analysis.
    return raw ? getNumericBoqVersion((JSON.parse(raw) as { version?: number | string })?.version ?? 0) : 0;
  })()).current;

  // Retaining-walls and geowalls are groundworks-only — every consumer of this
  // hook's totals (MainLayout's save/idle-save/nav-block handlers, StepperNavigation)
  // only actually reads them on /groundworks/* routes, so only fetch there. Fetching
  // on any other route (roads-sewers pages, the /tender/* creation flow, dashboard,
  // document-library, etc.) hit these APIs for values nothing ever displays or saves.
  const isGroundworksRoute = pathname.startsWith("/groundworks/");

  // Always refetch on mount — the take-off forms save via direct API POSTs
  // that bypass the React Query mutation hook's onSuccess invalidation, so
  // cached specs can be stale and Sum Analysis / sum-analysis-derived totals
  // would diverge from the latest take-off / build-up data.
  const { data: retWallsBuildUp } = useGetRetainingWallsBuildUp(tenderName, boqVersionNumber, {
    enabled: !!tenderName && isGroundworksRoute,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const { data: geoToberWallBuildUp } = useGetGeoToberWallBuildUp(tenderName, boqVersionNumber, {
    enabled: !!tenderName && isGroundworksRoute,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const ratesMapping: RateMappingJSON | null = null;

  // Calculate all totals using the centralized calculateSumAnalysisGrandTotal function
  const totals = useMemo<GroundworksTotals>(() => {
    const groundworksData = state.groundworks;
    const preliminariesPercent = globalSettings.groundworks.preliminaries || 0;
    const ohpPercent = globalSettings.groundworks.overheadAndProfit || 0;
    const discountPercent = globalSettings.groundworks.discount || 0;

    // Use the centralized calculation function
    const calculationResult = calculateSumAnalysisGrandTotal({
      groundworksData,
      ratesMapping,
      itemLibraryData,
      preliminariesPercent,
      ohpPercent,
      discountPercent,
      includePreliminaries,
      includeOhp,
      includeDiscount,
      retWallsBuildUpSpecs: retWallsBuildUp?.specifications,
      geoToberWallBuildUpSpecs: geoToberWallBuildUp?.specifications,
      enableLogging: false,
    });

    // Transform the result to match our interface
    const sections: SectionTotal[] = calculationResult.sections.map((section) => ({
      name: section.sectionName,
      stepName: section.stepName,
      baseAmount: section.baseAmount,
      preliminariesAmount: section.preliminariesAmount,
      ohpAmount: section.ohpAmount,
      totalAmount: section.totalAmount,
    }));

    return {
      sections,
      grandTotal: calculationResult.baseGrandTotal,
      grandTotalWithAdjustments: calculationResult.grandTotalWithAdjustments,
      discountAmount: calculationResult.discountAmount,
      tenderTotal: calculationResult.tenderTotal,
      preliminariesPercent: calculationResult.preliminariesPercent,
      ohpPercent: calculationResult.ohpPercent,
      discountPercent: calculationResult.discountPercent,
    };
  }, [
    state.groundworks,
    itemLibraryData,
    globalSettings.groundworks.preliminaries,
    globalSettings.groundworks.overheadAndProfit,
    globalSettings.groundworks.discount,
    includePreliminaries,
    includeOhp,
    includeDiscount,
    recalculateTrigger,
    retWallsBuildUp?.specifications,
    geoToberWallBuildUp?.specifications,
  ]);

  return totals;
};

/**
 * Helper function to format currency value
 */
export const formatCurrency = (value: number): string => {
  return `£${value.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Helper function to get section total by step name
 */
export const getSectionByStepName = (
  totals: GroundworksTotals,
  stepName: string
): SectionTotal | undefined => {
  return totals.sections.find((section) => section.stepName === stepName);
};

export default useGroundworksTotalCalculations;
